import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Palette } from "@phosphor-icons/react";
import { useFetch } from "@/lib/api";
import { useQuote } from "@/lib/quote";
import { EASE, FadeUp } from "@/lib/motion";
import ProductCard from "@/components/ProductCard";
import { SITE, IMG, HOW_IT_WORKS, FAQS, REGIONS, GALLERY, ROMAN } from "@/data/content";

const guestSlugs = ["perfume-bar", "wedding-concierge", "coat-check", "vintage-photo-booth"];

function SectionHead({ eyebrow, title, text }) {
  return (
    <FadeUp>
      <div className="flex items-center gap-4">
        <span className="h-px w-10 bg-gilt" aria-hidden="true" />
        <p className="mv-label mb-0">{eyebrow}</p>
      </div>
      <h2 className="mt-4 max-w-2xl font-display text-4xl font-light leading-[1.08] text-ink sm:text-5xl">{title}</h2>
      {text && <p className="mt-5 max-w-xl leading-relaxed text-smoke">{text}</p>}
    </FadeUp>
  );
}

function MatFrame({ src, alt, ratio = "aspect-[4/5]", width = 800, height = 1000, children, testid }) {
  return (
    <div className="mat-frame" data-testid={testid}>
      <div className="relative overflow-hidden">
        <img src={src} alt={alt} className={`${ratio} w-full object-cover`} width={width} height={height} loading="lazy" />
        {children}
      </div>
    </div>
  );
}

export default function Home() {
  const { data: catalog } = useFetch("/catalog");
  const { data: services } = useFetch("/services");
  const { data: collections } = useFetch("/collections");
  const { setAtelierOpen } = useQuote();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -36]);

  const categories = catalog ? [...new Set(catalog.map((c) => c.category))] : [];
  const guestServices = (services || []).filter((s) => guestSlugs.includes(s.slug));
  const decorServices = (services || []).filter((s) => !guestSlugs.includes(s.slug));

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-[76px]" data-testid="home-hero">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 pb-20 pt-12 sm:px-8 lg:grid-cols-12 lg:items-center lg:gap-8 lg:pt-8">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.15 }} className="flex items-center gap-4">
              <span className="h-px w-12 bg-gilt" aria-hidden="true" />
              <p className="mv-label mb-0">Marble &amp; Veil · Southern California</p>
            </motion.div>
            <h1 className="mt-8 font-display text-[12.5vw] font-light leading-[1.04] tracking-tight text-ink sm:text-7xl lg:text-[5.6rem]" data-testid="home-hero-title">
              {["Beautiful wedding", "details, thoughtfully", "brought together."].map((line, i) => (
                <span key={i} className="block overflow-hidden pb-[0.16em] -mb-[0.16em]" aria-hidden="true">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1.1, ease: EASE, delay: 0.3 + i * 0.14 }}
                  >
                    {i === 1 ? (
                      <>
                        details, <em className="italic text-olive">thoughtfully</em>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
              <span className="sr-only">Beautiful wedding details, thoughtfully brought together.</span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
              className="mt-8 max-w-xl text-lg leading-relaxed text-smoke"
              data-testid="home-hero-support"
            >
              Personalized pieces, coordinated decor, and guest experiences for Southern California
              celebrations. Choose the details you love, and let us help bring them together.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to="/shop" data-testid="home-cta-explore" className="border border-olive bg-olive px-9 py-4 text-[12px] uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-transparent hover:text-olive">
                Explore the Collection
              </Link>
              <Link to="/quote" data-testid="home-cta-quote" className="border border-ink/25 px-9 py-4 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-olive hover:text-olive">
                Request a Quote
              </Link>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.3, ease: EASE, delay: 0.5 }}
                className="mat-frame"
              >
                <div className="overflow-hidden">
                  <img
                    src={IMG.hero}
                    alt="A bride in a sheer vintage sleeve — design inspiration"
                    className="aspect-[3/4] w-full object-cover"
                    width="900"
                    height="1200"
                    data-testid="home-hero-image"
                  />
                </div>
              </motion.div>
              <motion.div
                style={{ y: cardY }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.35 }}
                className="absolute -bottom-8 -left-4 hidden w-40 sm:block lg:-left-14"
              >
                <div className="mat-frame p-3">
                  <img src={IMG.waxSeal} alt="Personalized stationery detail — design inspiration" className="aspect-square w-full object-cover" width="176" height="176" loading="lazy" />
                  <p className="mt-2 text-center text-[10px] uppercase tracking-widest2 text-smoke">Design inspiration</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="double-rule" aria-hidden="true" />

      {/* COMBINED OFFER — white with oil-painting art */}
      <section className="bg-porcelain">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHead eyebrow="One Thoughtful Place" title={<>Everything your guests touch, <em className="italic text-olive">gathered</em> under one roof.</>} text="Marble & Veil brings together personalized wedding goods, coordinated decor, and thoughtful guest experiences — so the details speak the same language." />
            </div>
            <div className="grid gap-px bg-ink/10 sm:grid-cols-3 lg:col-span-8">
              {[
                ["Personalized goods", "Signage, stationery, napkins, and cups designed around your monogram and palette.", "/shop"],
                ["Coordinated decor", "Styled tablescapes, linen styling, centerpieces, and fresh florals for ceremony and reception.", "/services/tablescapes-decor-florals"],
                ["Guest experiences", "A staffed perfume bar, wedding concierge, and coat check — hospitality you can hand off.", "/services"],
              ].map(([t, d, to], i) => (
                <FadeUp key={t} delay={i * 0.1} className="bg-porcelain">
                  <Link to={to} className="relative flex h-full flex-col overflow-hidden bg-porcelain" data-testid={`home-offer-${i}`}>
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-cover bg-center opacity-[0.55]"
                      style={{ backgroundImage: `url(${[IMG.oilArt1, IMG.oilArt2, IMG.oilArt3][i]})`, filter: 'brightness(0.8) saturate(0.92)' }}
                    />
                    <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-tr from-porcelain/70 via-porcelain/40 to-porcelain/15" />
                    <span className="relative flex h-full flex-col p-8">
                      <span className="font-display text-3xl italic text-olive">{ROMAN[i]}</span>
                      <h3 className="mt-5 font-display text-2xl font-light text-ink">{t}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-smoke">{d}</p>
                      <span className="mt-auto flex items-center gap-2 pt-6 text-[11px] uppercase tracking-[0.18em] text-olive">
                        Explore <ArrowRight size={13} weight="light" />
                      </span>
                    </span>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SHOP CATEGORIES */}
      <section className="bg-porcelain">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow="The Collection" title="Start with a single detail." />
            <FadeUp delay={0.15}>
              <Link to="/shop" data-testid="home-shop-all-link" className="border-b border-olive pb-1 text-[12px] uppercase tracking-[0.18em] text-olive">
                View the whole shop
              </Link>
            </FadeUp>
          </div>
          <div className="mt-14 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {(catalog || []).slice(0, 4).map((item, i) => (
              <FadeUp key={item.slug} delay={i * 0.08}>
                <ProductCard item={item} priority={i < 2} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FRESH FLORALS */}
      <section className="bg-ecru" data-testid="home-florals-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead
              eyebrow="Fresh Florals"
              title={<>Real flowers, <em className="italic text-olive">sourced per request</em>.</>}
              text="Fresh flowers only — never artificial or silk — arranged by hand for the reception and ceremony, sourced per request, subject to seasonal availability."
            />
            <FadeUp delay={0.15}>
              <Link to="/shop?category=Fresh%20Florals" data-testid="home-florals-link" className="border-b border-olive pb-1 text-[12px] uppercase tracking-[0.18em] text-olive">
                View the florals
              </Link>
            </FadeUp>
          </div>
          <div className="mt-14 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {(catalog || []).filter((c) => c.category === "Fresh Florals").slice(0, 4).map((item, i) => (
              <FadeUp key={item.slug} delay={i * 0.08}>
                <ProductCard item={item} priority={i === 0} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* GUEST EXPERIENCES */}
      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <SectionHead eyebrow="Guest Experiences" title="Hospitality you can hand off." text="Staffed services that care for your people — arranged around your venue, scoped in your proposal." />
          <div className="mt-16 space-y-16 lg:space-y-24">
            {guestServices.map((s, i) => (
              <FadeUp key={s.slug}>
                <div className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <Link to={`/services/${s.slug}`} className="mat-frame block transition-colors duration-500 hover:border-gilt/60" data-testid={`home-guest-${s.slug}`}>
                    <div className="overflow-hidden">
                      <img src={s.image} alt={`${s.name} — design inspiration`} className="aspect-[4/3] w-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.03]" width="900" height="675" loading="lazy" />
                    </div>
                  </Link>
                  <div>
                    {s.status === "coming_soon" && (
                      <span className="inline-block border border-olive/50 px-3 py-1 text-[10px] uppercase tracking-widest2 text-olive" data-testid={`coming-soon-${s.slug}`}>
                        Coming soon
                      </span>
                    )}
                    <h3 className="mt-3 font-display text-3xl font-light text-ink sm:text-4xl">{s.name}</h3>
                    <p className="mt-2 font-display text-xl italic text-olive">{s.tagline}</p>
                    <p className="mt-4 max-w-lg leading-relaxed text-smoke">{s.short}</p>
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`home-guest-cta-${s.slug}`}
                      className="mt-7 inline-block border border-ink/25 px-8 py-3.5 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-olive hover:bg-olive hover:text-ivory"
                    >
                      {s.status === "coming_soon" ? "Ask About Future Availability" : "Request Availability"}
                    </Link>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* DECOR / SIGNAGE / FLORALS — estate treatment */}
      <section className="bg-linen" data-testid="home-decor-section">
        <div className="double-rule" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <SectionHead eyebrow={<>Decor · Signage · Florals</>} title={<>The room, styled as <em className="italic text-olive">one thought</em>.</>} />
          <div className="mt-14 grid gap-px bg-ink/10 md:grid-cols-3">
            {decorServices.map((s, i) => (
              <FadeUp key={s.slug} delay={i * 0.1} className="bg-linen">
                <Link to={`/services/${s.slug}`} data-testid={`home-decor-${s.slug}`} className="group flex h-full flex-col border border-ink/10 p-8 transition-colors duration-300 hover:bg-porcelain">
                  <span className="font-display text-3xl italic text-olive">{ROMAN[i]}</span>
                  <h3 className="mt-5 font-display text-2xl font-light text-ink">{s.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-smoke">{s.short}</p>
                  <span className="mt-auto flex items-center gap-2 pt-6 text-[11px] uppercase tracking-[0.18em] text-ink/60 transition-colors group-hover:text-olive">
                    Request Availability <ArrowRight size={13} weight="light" />
                  </span>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow="Curated Collections" title="Ideas to begin with — not packages to settle for." />
            <FadeUp delay={0.15}>
              <Link to="/collections" data-testid="home-collections-all-link" className="border-b border-olive pb-1 text-[12px] uppercase tracking-[0.18em] text-olive">
                All collections
              </Link>
            </FadeUp>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {(collections || []).map((c, i) => (
              <FadeUp key={c.slug} delay={i * 0.1}>
                <Link to={`/collections/${c.slug}`} className="group block" data-testid={`home-collection-${c.slug}`}>
                  <div className="mat-frame transition-colors duration-500 group-hover:border-gilt/60">
                    <div className="overflow-hidden">
                      <img src={c.image} alt={`${c.name} collection concept — design inspiration`} className="aspect-[4/5] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]" width="600" height="750" loading="lazy" />
                    </div>
                  </div>
                  <div className="px-1 pt-5">
                    <h3 className="font-display text-2xl font-light text-ink transition-colors group-hover:text-olive">{c.name}</h3>
                    <p className="mt-1 text-sm text-smoke">{c.short}</p>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PALETTE ATELIER — marble band */}
      <section className="marble-band border-y border-ink/10">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:py-32">
          <div>
            <FadeUp>
              <div className="flex items-center gap-4">
                <span className="h-px w-10 bg-gilt" aria-hidden="true" />
                <p className="mv-label mb-0">The Palette Atelier</p>
              </div>
              <h2 className="mt-4 font-display text-4xl font-light leading-[1.08] text-ink sm:text-5xl">
                We test the waters on color <em className="italic text-olive">together</em>.
              </h2>
              <p className="mt-5 max-w-lg leading-relaxed text-smoke">
                Play with palettes, try your monogram, and save what feels right. Our design offers
                templates we work around to keep things simple — and personalization is created
                together, so nothing feels borrowed.
              </p>
              <button
                data-testid="home-atelier-open"
                onClick={() => setAtelierOpen(true)}
                className="mt-9 inline-flex items-center gap-3 border border-olive bg-olive px-9 py-4 text-[12px] uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-transparent hover:text-olive"
              >
                <Palette size={16} weight="light" /> Launch the Atelier
              </button>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <div className="grid grid-cols-3 gap-3" aria-hidden="true">
              {["#FCFCFA", "#E5D3B3", "#D9D5C5", "#8A8A5C", "#67672D", "#33322E"].map((c, i) => (
                <div key={i} className="flex aspect-square items-end border border-ink/10 p-3" style={{ background: c }}>
                  <span className={`text-[10px] uppercase tracking-widest2 ${i > 3 ? "text-ivory/70" : "text-ink/40"}`}>{ROMAN[i]}</span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-porcelain">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <SectionHead eyebrow="How It Works" title="Simple, honest, unhurried." />
          <div className="mt-14 grid gap-px bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((s, i) => (
              <FadeUp key={s.step} delay={i * 0.08} className="bg-porcelain">
                <div className="flex h-full flex-col border border-ink/10 p-8" data-testid={`how-it-works-${s.step}`}>
                  <span className="font-display text-3xl italic text-olive">{s.step}</span>
                  <h3 className="mt-5 font-display text-xl font-light leading-snug text-ink">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-smoke">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="bg-ecru">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:py-32">
          <FadeUp>
            <div className="mat-frame flex aspect-[4/5] flex-col items-center justify-center text-center" data-testid="home-family-card">
              <img src="/marble-and-veil-logo.png" alt="Marble & Veil" className="h-24 w-auto" width="190" height="96" loading="lazy" />
              <div className="double-rule my-7 w-20" aria-hidden="true" />
              <p className="px-10 text-[11px] uppercase tracking-widest2 text-smoke">From our family to yours</p>
              <p className="mt-3 px-10 font-display text-4xl font-light italic leading-tight text-olive" data-testid="home-family-motto">
                Two families. One goal.
              </p>
              <p className="mt-4 max-w-[280px] px-8 font-display text-xl italic leading-snug text-ink/80">
                Bringing the elegance to brides and grooms — without the stress of multiple vendors.
              </p>
              <p className="mt-6 max-w-[260px] px-8 text-[12px] leading-relaxed text-smoke">
                Family owned &amp; operated in Southern California — every detail passes through our hands.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mv-label">Our Story</p>
            <h2 className="mt-3 font-display text-4xl font-light leading-[1.08] text-ink sm:text-5xl">A family business, built around your wedding.</h2>
            <p className="dropcap mt-7 max-w-xl leading-relaxed text-smoke">
              Marble &amp; Veil began with planning our own weddings. We saw how quickly the costs and
              decisions could add up, especially when every detail meant finding another vendor. That
              experience shaped a simple idea: bring beautiful, personal wedding details together in a
              more thoughtful, approachable way. From the first welcome to the tablescape and guest
              experience, we help couples create a celebration that feels like theirs.
            </p>
            <p className="mt-5 max-w-xl leading-relaxed text-smoke">
              Marble &amp; Veil is a family business — Sandra &amp; Nathan and Alexandra &amp; Roman.
              When you write to us, you reach us directly, and the same family sees your details
              through from first sketch to final placement.
            </p>
            <p className="mt-6 font-display text-2xl italic text-olive">{SITE.owners}</p>
            <Link to="/about" data-testid="home-about-link" className="mt-8 inline-block border border-ink/25 px-8 py-3.5 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-olive hover:text-olive">
              Read More About Us
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow="The Gallery" title="A feeling, before it is a plan." text="Design inspiration imagery while our real portfolio grows with each celebration." />
            <FadeUp delay={0.15}>
              <Link to="/gallery" data-testid="home-gallery-link" className="border-b border-olive pb-1 text-[12px] uppercase tracking-[0.18em] text-olive">
                View the gallery
              </Link>
            </FadeUp>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-6 lg:grid-cols-4 lg:gap-8">
            {GALLERY.slice(0, 4).map((g, i) => (
              <FadeUp key={g.src} delay={i * 0.07}>
                <figure className="group">
                  <div className="mat-frame transition-colors duration-500 group-hover:border-gilt/60">
                    <div className="overflow-hidden">
                      <img src={g.src} alt={g.caption} className="aspect-[4/5] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]" width="600" height="750" loading="lazy" />
                    </div>
                  </div>
                  <figcaption className="mt-3 text-[11px] uppercase tracking-[0.16em] text-smoke">
                    {g.tag} · {g.caption}
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="bg-linen">
        <div className="double-rule" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow="Where We Work" title="Across Southern California." />
            <FadeUp delay={0.15}>
              <Link to="/service-area" data-testid="home-area-link" className="border-b border-olive pb-1 text-[12px] uppercase tracking-[0.18em] text-olive">
                Full service area
              </Link>
            </FadeUp>
          </div>
          <div className="mt-12 grid gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {REGIONS.map((r, i) => (
              <FadeUp key={r.name} delay={i * 0.05}>
                <p className="font-display text-xl text-ink/80">
                  <span className="mr-3 font-display text-base italic text-olive">{ROMAN[i]}</span>
                  {r.name}
                </p>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-smoke">
              Availability depends on your date, scope, staffing, and travel. We do not publish venue
              partnerships or fixed travel fees — every plan is scoped personally.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-ivory">
        <div className="mx-auto max-w-3xl px-5 py-24 sm:px-8 lg:py-32">
          <SectionHead eyebrow="Practical Questions" title="Good to know." />
          <div className="mt-12 divide-y divide-ink/10 border-y border-ink/10">
            {FAQS.map((f, i) => (
              <FadeUp key={f.q} delay={i * 0.04}>
                <details className="group py-5" data-testid={`faq-item-${i}`}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-xl font-light text-ink transition-colors hover:text-olive">
                    {f.q}
                    <span className="text-gilt transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 leading-relaxed text-smoke">{f.a}</p>
                </details>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA — marble */}
      <section className="marble-band border-t border-ink/10" data-testid="home-final-cta">
        <div className="mx-auto max-w-3xl px-5 py-24 text-center sm:px-8 lg:py-32">
          <FadeUp>
            <img src="/marble-and-veil-logo.png" alt="Marble & Veil" className="mx-auto h-16 w-auto" width="210" height="56" loading="lazy" />
            <h2 className="mt-8 font-display text-4xl font-light leading-[1.08] text-ink sm:text-6xl">Choose the details you love.</h2>
            <p className="mx-auto mt-5 max-w-xl leading-relaxed text-smoke">
              Build a quote list, test a palette, and tell us about your celebration. Pricing is
              confirmed in your proposal — and nothing is final until you say so.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/quote" data-testid="home-final-quote-link" className="border border-olive bg-olive px-9 py-4 text-[12px] uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-transparent hover:text-olive">
                Request a Quote
              </Link>
              <Link to="/contact" className="border border-ink/30 px-9 py-4 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-olive hover:text-olive">
                Contact Us
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
